import React, { useState, useEffect } from 'react'; // eslint-disable-line
import * as XLSX from 'xlsx';
import { api } from './shared';
import PlayersTab from './PlayersTab';
import TeamsTab from './TeamsTab';
import TransactionsTab from './TransactionsTab';

const CampaignReportTab = ({ onError }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try { const r = await api('campaign-report'); setReport(r.data); }
    catch (e) { onError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const exportExcel = async () => {
    setExporting(true);
    try {
      const [playersRes, teamsRes, txRes, storesBuRes, newBpRes] = await Promise.all([
        api('players'),
        api('teams'),
        api('transactions'),
        api('stores-bu-report'),
        api('new-bigpoint-active'),
      ]);
      const r = report;
      const wb = XLSX.utils.book_new();

      // Sheet 1: ภาพรวม
      const kpiRows = [
        ['Campaign Report'],
        ['เริ่ม', r.campaign.startDate],
        ['วันนี้', r.campaign.currentDate],
        [],
        ['KPI', 'จำนวน'],
        ['ผู้เล่นทั้งหมด (unique)', r.kpi.totalUniquePlayers],
        ['New Member (ได้ x2)', r.players?.newMemberX2 ?? 0],
        ['ทีมทั้งหมด', r.kpi.totalTeams],
        ['ทีมครบ 4 คน', r.kpi.totalTeamsComplete],
        ['บิลทั้งหมด', r.kpi.totalBills],
        ['บิลสำเร็จ', r.kpi.totalReceipts],
        ['บิลรอคิว', r.kpi.totalPendings],
        ['ยอดสำเร็จ', r.kpi.receiptSpend],
        ['ยอดรอคิว', r.kpi.pendingSpend],
        ['ยอดรวม', r.kpi.totalSpend],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(kpiRows), 'ภาพรวม');

      // Sheet 2: Funnel
      const funnelRows = [
        ['ขั้นตอน', 'จำนวน (คน)', '% จาก newMember'],
        ['1. ผูก BigPoint (newMember)', r.funnel.linkedBigPoint, '100%'],
        ['2. เข้าหน้า BTS', r.funnel.enteredBTS, r.funnel.linkedBigPoint > 0 ? `${(r.funnel.enteredBTS / r.funnel.linkedBigPoint * 100).toFixed(1)}%` : '0%'],
        ['3. Join / สร้างทีม', r.funnel.joinedTeam, r.funnel.linkedBigPoint > 0 ? `${(r.funnel.joinedTeam / r.funnel.linkedBigPoint * 100).toFixed(1)}%` : '0%'],
        ['4. สแกนบิลสำเร็จ', r.funnel.scannedSuccess, r.funnel.linkedBigPoint > 0 ? `${(r.funnel.scannedSuccess / r.funnel.linkedBigPoint * 100).toFixed(1)}%` : '0%'],
        ['5. ทีมครบ 4 คน', r.funnel.teamsComplete, r.funnel.linkedBigPoint > 0 ? `${(r.funnel.teamsComplete / r.funnel.linkedBigPoint * 100).toFixed(1)}%` : '0%'],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(funnelRows), 'Funnel');

      // Sheet 3: ผู้เล่นทั้งหมด — filter ด้วย kpiPlayerIds เพื่อให้ count ตรงกับ KPI
      const p = playersRes.data;
      const kpiIds = new Set(r.kpiPlayerIds || []);
      const allPlayers = [
        ...p.inTeam.map(x => ({ ...x, type: parseInt(x.receipt_count || 0) > 0 ? 'คะแนนสะสมสำเร็จ' : 'ในทีม ยังไม่สแกน' })),
        ...p.waitingForTeam.map(x => ({ ...x, type: 'รอสร้างทีม', party_name: '-', personal_tickets: 0, is_owner: false, joined_at: x.last_scanned_at })),
        ...(p.receiptOnly || []).map(x => ({ ...x, type: 'สแกนแล้ว (ไม่มีทีม)', party_name: '-', personal_tickets: 0, is_owner: false, joined_at: x.last_scanned_at })),
      ];
      // เพิ่ม register-only newMembers (dedupe)
      const seenP = new Set(allPlayers.map(x => x.player_id));
      (p.newMembers || []).forEach(x => {
        if (!seenP.has(x.player_id)) {
          allPlayers.push({ ...x, type: 'ผูก BigPoint', party_name: x.party_name || '-', personal_tickets: 0, is_owner: false, joined_at: x.bigpoint_linked_at });
          seenP.add(x.player_id);
        }
      });
      // กรองให้เหลือเฉพาะ player ที่อยู่ใน KPI (ผูก BigPoint + valid + ถึงเมื่อวาน)
      const kpiPlayers = allPlayers.filter(x => kpiIds.has(parseInt(x.player_id)));
      const playerRows = [
        ['สถานะ','ชื่อ','BigPoint','ทีม','คะแนน','เจ้าของ','บิลสำเร็จ','บิลรอคิว','เข้าร่วม'],
        ...kpiPlayers.map(x => [
          x.type,
          x.display_name || '',
          x.bigpoint_id || '',
          x.party_name || '-',
          parseFloat(x.personal_tickets || 0),
          x.is_owner ? 'ใช่' : '',
          parseInt(x.receipt_count || x.receiptCount || 0),
          parseInt(x.pending_count || x.pendingCount || 0),
          x.joined_at ? new Date(x.joined_at).toLocaleString('th-TH') : '-',
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(playerRows), 'ผู้เล่น');

      // Sheet: ผู้เล่นใหม่ (BigPoint) — list newMembers ตั้งแต่เริ่ม campaign
      const newMembersList = p.newMembers || [];
      const newMemberRows = [
        ['#','ชื่อ','BigPoint','ผูก BigPoint เมื่อ','ทีม','คะแนน','เจ้าของ','New Member (x2)','บิลสำเร็จ','บิลรอคิว','ยอดสะสม (บาท)'],
        ...newMembersList.map((x, i) => {
          const spend = parseFloat(x.receipt_spend || 0) + parseFloat(x.pending_spend || 0);
          return [
            i + 1,
            x.display_name || '',
            x.bigpoint_id || '',
            x.bigpoint_linked_at ? new Date(x.bigpoint_linked_at).toLocaleString('th-TH') : '-',
            x.party_name || '-',
            parseFloat(x.personal_tickets || 0),
            x.is_owner ? 'ใช่' : '',
            x.is_new_member ? 'ใช่' : '',
            parseInt(x.receipt_count || 0),
            parseInt(x.pending_count || 0),
            spend,
          ];
        }),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(newMemberRows), 'ผู้เล่นใหม่ (BigPoint)');

      // Sheet 4: ทีมทั้งหมด
      const teams = teamsRes.data.data || [];
      const teamRows = [
        ['อันดับ','ชื่อทีม','สมาชิก','คะแนนรวม','สถานะ','สร้างเมื่อ'],
        ...teams.map((t, i) => [
          i + 1,
          t.party_name,
          `${t.member_count}/4`,
          parseFloat(t.total_tickets || 0),
          t.status,
          new Date(t.created_at).toLocaleString('th-TH'),
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(teamRows), 'ทีม');

      // Sheet 5: บิล (exclude cancelled + filter CUTOFF + ตัด orphan store ให้ตรง KPI)
      const validStoreIds = new Set((r.stores || []).map(s => String(s.storeId)));
      const cutoffMs = r.campaign?.cutoffIso ? new Date(r.campaign.cutoffIso).getTime() : Infinity;
      const tx = (txRes.data.data || []).filter(t =>
        t.status !== 'reverted'
        && new Date(t.date).getTime() < cutoffMs
        && validStoreIds.has(String(t.store))
      );
      const billRows = [
        ['สถานะ','วันที่','ชื่อ','CusId','เลขบิล','Partner','สาขา','ยอด','คะแนน','ประเภท'],
        ...tx.map(t => [
          t.statusLabel,
          new Date(t.date).toLocaleString('th-TH'),
          t.displayName || '',
          t.cusId,
          t.receiptNo,
          t.partner,
          t.store,
          parseFloat(t.sale || 0),
          parseFloat(t.tickets || 0),
          t.type,
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(billRows), 'บิล');

      // Sheet 6: Team breakdown + Sale breakdown
      if (r.teamBreakdown) {
        const tb = r.teamBreakdown;
        const totTeams = tb.complete.withBills.teamCount + tb.complete.noBills.teamCount + tb.incomplete.withBills.teamCount + tb.incomplete.noBills.teamCount;
        const totSpend = tb.complete.withBills.totalSpend + tb.complete.noBills.totalSpend + tb.incomplete.withBills.totalSpend + tb.incomplete.noBills.totalSpend + tb.noTeam.withBills.totalSpend + tb.noTeam.noBills.totalSpend;
        const breakdownRows = [
          ['Total Team', '', '', '', 'Total Sale', '', ''],
          ['รายละเอียด', 'จำนวน', 'หน่วย', '', 'รายละเอียด', 'จำนวน', 'หน่วย'],
          ['1. ตั้งชื่อทีม (ทั้งหมด)', totTeams, 'ทีม', '', '1. ยอดซื้อสะสม ทั้งหมด', totSpend, 'บาท'],
          ['  1.1) ตั้งทีม สำเร็จ 4 คน', tb.complete.withBills.teamCount + tb.complete.noBills.teamCount, 'ทีม', '', '  1.1) ตั้งทีม สำเร็จ 4 คน', tb.complete.withBills.totalSpend + tb.complete.noBills.totalSpend, 'บาท'],
          ['    เริ่ม สะสม', tb.complete.withBills.teamCount, 'ทีม', '', '    เริ่ม สะสม', tb.complete.withBills.totalSpend, 'บาท'],
          ['    ไม่ สะสม', tb.complete.noBills.teamCount, 'ทีม', '', '    ไม่ สะสม', tb.complete.noBills.totalSpend, 'บาท'],
          ['  1.2) ตั้งทีม ไม่สำเร็จ', tb.incomplete.withBills.teamCount + tb.incomplete.noBills.teamCount, 'ทีม', '', '  1.2) ตั้งทีม ไม่สำเร็จ', tb.incomplete.withBills.totalSpend + tb.incomplete.noBills.totalSpend, 'บาท'],
          ['    เริ่ม สะสม', tb.incomplete.withBills.teamCount, 'ทีม', '', '    เริ่ม สะสม', tb.incomplete.withBills.totalSpend, 'บาท'],
          ['    ไม่ สะสม', tb.incomplete.noBills.teamCount, 'ทีม', '', '    ไม่ สะสม', tb.incomplete.noBills.totalSpend, 'บาท'],
          ['2. ไม่สร้างทีม (คน)', tb.noTeam.withBills.playerCount + tb.noTeam.noBills.playerCount, 'คน', '', '2. ไม่สร้างทีม', tb.noTeam.withBills.totalSpend + tb.noTeam.noBills.totalSpend, 'บาท'],
          ['  เริ่ม สะสม', tb.noTeam.withBills.playerCount, 'คน', '', '  เริ่ม สะสม', tb.noTeam.withBills.totalSpend, 'บาท'],
          ['  ไม่ สะสม', tb.noTeam.noBills.playerCount, 'คน', '', '  ไม่ สะสม', '-', 'บาท'],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(breakdownRows), 'Team-Sale Breakdown');
      }

      // Sheet 7: รายสาขา (1 row ต่อสาขา — รวม partner เป็นสาขาเดียว)
      const stores = r.stores || [];
      const totalStores = stores.length;
      const totalBills = stores.reduce((s, x) => s + x.totalBills, 0);
      const totalSuccess = stores.reduce((s, x) => s + x.successBills, 0);
      const totalPending = stores.reduce((s, x) => s + x.pendingBills, 0);
      const totalSpendStores = stores.reduce((s, x) => s + x.totalSpend, 0);

      const storeRows = [
        ['สรุปสาขา', '', '', '', '', '', '', ''],
        ['จำนวนสาขาที่มียอด', totalStores, 'สาขา', '', '', '', '', ''],
        ['บิลรวม', totalBills, '(สำเร็จ ' + totalSuccess + ' / รอคิว ' + totalPending + ')', '', '', '', '', ''],
        ['ยอดรวม', totalSpendStores, 'บาท', '', '', '', '', ''],
        [],
        ['รายละเอียดสาขา', '', '', '', '', '', '', '', ''],
        ['อันดับ','รหัสสาขา','ชื่อสาขา','Partner','บิลทั้งหมด','สำเร็จ','รอคิว','ผู้เล่น (unique)','ยอดรวม'],
        ...stores.map((s, i) => [
          i + 1,
          s.storeId,
          s.storeName,
          s.partner || '-',
          s.totalBills,
          s.successBills,
          s.pendingBills,
          s.uniquePlayers,
          s.totalSpend,
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(storeRows), 'รายสาขา');

      // Sheet 7b: รายสาขา (BU) — เรียงตาม master sheet (row#, store_id, BU)
      const storesBu = storesBuRes.data || [];
      const buHeader = ['Store', '', 'SITE_NAME', 'BU', 'customer', 'ticket', 'Sales'];
      const buBody = storesBu.map(s => [
        s.row,
        s.storeId,
        s.siteName,
        s.bu,
        s.customer || '-',
        s.ticket || '-',
        s.sales ? Math.round(s.sales) : '-',
      ]);
      const buTotal = storesBu.reduce((acc, s) => ({
        customer: acc.customer + (s.customer || 0),
        ticket: acc.ticket + (s.ticket || 0),
        sales: acc.sales + (s.sales || 0),
      }), { customer: 0, ticket: 0, sales: 0 });
      const buRows = [
        buHeader,
        ...buBody,
        ['Total', '', '', '', buTotal.customer, buTotal.ticket, Math.round(buTotal.sales)],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buRows), 'รายสาขา (BU)');

      // Sheet 7c: New BigPoint Active — สมัคร BigPoint ในช่วง campaign + มี activity
      const newBp = newBpRes.data || [];
      const newBpRows = [
        ['#', 'ชื่อ', 'BigPoint', 'สมัคร BigPoint เมื่อ', 'New Member (x2)', 'ทีม', 'ทีมครบ?', 'เจ้าของทีม', 'คะแนน', 'บิลสำเร็จ', 'บิลรอคิว', 'ยอดสำเร็จ', 'ยอดรอคิว', 'ยอดรวม', 'เข้าทีมเมื่อ'],
        ...newBp.map((p, i) => [
          i + 1,
          p.displayName || '',
          p.bigpointId || '',
          p.bigpointRegisteredAt ? new Date(p.bigpointRegisteredAt).toLocaleString('th-TH') : '-',
          p.isNewMember ? 'ใช่' : '',
          p.partyName || '-',
          p.partyMemberCount != null ? (p.partyMemberCount >= 4 ? 'ครบ' : `${p.partyMemberCount}/4`) : '-',
          p.isOwner ? 'ใช่' : '',
          p.personalTickets || 0,
          p.receiptCount || 0,
          p.pendingCount || 0,
          p.receiptSpend || 0,
          p.pendingSpend || 0,
          p.totalSpend || 0,
          p.joinedAt ? new Date(p.joinedAt).toLocaleString('th-TH') : '-',
        ]),
        ['', 'รวมทั้งหมด', `${newBp.length} คน`, '', '', '', '', '',
          newBp.reduce((s, p) => s + (parseFloat(p.personalTickets) || 0), 0),
          newBp.reduce((s, p) => s + (p.receiptCount || 0), 0),
          newBp.reduce((s, p) => s + (p.pendingCount || 0), 0),
          newBp.reduce((s, p) => s + (p.receiptSpend || 0), 0),
          newBp.reduce((s, p) => s + (p.pendingSpend || 0), 0),
          newBp.reduce((s, p) => s + (p.totalSpend || 0), 0),
          '',
        ],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(newBpRows), 'New BigPoint + Active');

      // Sheet 8: ผู้เข้าเกม (visit log) — ทุกคนที่เคยเข้า BTS (ตารางเดียว ไม่แยกสถานะ)
      const visits = r.visitLog || [];
      const visitRows = [
        ['#', 'ชื่อ', 'BigPoint', 'สถานะ', 'เข้าครั้งแรก', 'เข้าครั้งล่าสุด', 'Dashboard visits', 'Total events'],
        ...visits.map((v, i) => [
          i + 1,
          v.displayName || '',
          v.bigpointId || '',
          v.status,
          v.firstVisit ? new Date(v.firstVisit).toLocaleString('th-TH') : '',
          v.lastVisit ? new Date(v.lastVisit).toLocaleString('th-TH') : '',
          v.dashboardVisits,
          v.totalEvents,
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(visitRows), 'ผู้เข้าเกม');

      // Sheet: Total Sale (ยอดซื้อที่ Scan เข้าระบบ) — breakdown ตามสถานะทีม
      if (r.teamBreakdown) {
        const tb = r.teamBreakdown;
        const c = tb.complete, ic = tb.incomplete, nt = tb.noTeam;
        const completeSpend = c.withBills.totalSpend + c.noBills.totalSpend;
        const completeBills = c.withBills.billCount + c.noBills.billCount;
        const incompleteSpend = ic.withBills.totalSpend + ic.noBills.totalSpend;
        const incompleteBills = ic.withBills.billCount + ic.noBills.billCount;
        const noTeamSpend = nt.withBills.totalSpend + nt.noBills.totalSpend;
        const noTeamBills = nt.withBills.billCount + nt.noBills.billCount;
        const grandSpend = completeSpend + incompleteSpend + noTeamSpend;
        const grandBills = completeBills + incompleteBills + noTeamBills;
        const avg = (s, b) => b > 0 ? (s / b) : '';
        const scanRows = [
          ['Total Sale (ยอดซื้อที่ Scan เข้าระบบ)'],
          ['รายละเอียด','จำนวน','หน่วย','จำนวนใบเสร็จ','หน่วย','Avg.Sale / ใบเสร็จ'],
          ['KPI', 1800000, 'บาท', 3000, 'ใบเสร็จ', ''],
          ['1. ยอดซื้อสะสม ทั้งหมด', grandSpend, 'บาท', grandBills, 'ใบเสร็จ', avg(grandSpend, grandBills)],
          ['  1.1) ตั้งทีม สำเร็จ 4 คน', completeSpend, 'บาท', completeBills, 'ใบเสร็จ', avg(completeSpend, completeBills)],
          ['    เริ่ม สะสม', c.withBills.totalSpend, 'บาท', c.withBills.billCount, 'ใบเสร็จ', avg(c.withBills.totalSpend, c.withBills.billCount)],
          ['    ไม่ สะสม', c.noBills.totalSpend, 'บาท', c.noBills.billCount, 'ใบเสร็จ', ''],
          ['  1.2) ตั้งทีม ไม่สำเร็จ', incompleteSpend, 'บาท', incompleteBills, 'ใบเสร็จ', avg(incompleteSpend, incompleteBills)],
          ['    เริ่ม สะสม', ic.withBills.totalSpend, 'บาท', ic.withBills.billCount, 'ใบเสร็จ', avg(ic.withBills.totalSpend, ic.withBills.billCount)],
          ['    ไม่ สะสม', ic.noBills.totalSpend, 'บาท', ic.noBills.billCount, 'ใบเสร็จ', ''],
          ['2. ไม่สร้างทีม', noTeamSpend, 'บาท', noTeamBills, 'ใบเสร็จ', avg(noTeamSpend, noTeamBills)],
          ['  เริ่ม สะสม', nt.withBills.totalSpend, 'บาท', nt.withBills.billCount, 'ใบเสร็จ', avg(nt.withBills.totalSpend, nt.withBills.billCount)],
          ['  ไม่ สะสม', nt.noBills.totalSpend, 'บาท', nt.noBills.billCount, 'ใบเสร็จ', ''],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(scanRows), 'Total Sale (Scan)');
      }

      // Sheet: Total Sale (เฉพาะกลุ่มที่ได้รางวัล)
      if (r.prizeStats) {
        const ps = r.prizeStats;
        const ac = ps.allComplete || { totalSpend: 0, billCount: 0 };
        const t5 = ps.top5 || { totalSpend: 0, billCount: 0 };
        const acAvg = ac.billCount > 0 ? ac.totalSpend / ac.billCount : 0;
        const t5Avg = t5.billCount > 0 ? t5.totalSpend / t5.billCount : 0;
        const prizeRows = [
          ['Total Sale (เฉพาะกลุ่มที่ได้รางวัล)'],
          ['รายละเอียด', 'จำนวน', 'หน่วย', 'จำนวนใบเสร็จ', 'Avg.Sale / ใบเสร็จ', 'หน่วย'],
          ['1.1) ตั้งทีม สำเร็จ 4 คน', ac.totalSpend, 'บาท', ac.billCount, acAvg, 'บาท'],
          [`ยอดคะแนนสะสม ${ps.topN} ทีมที่ได้รางวัล`, t5.totalSpend, 'บาท', t5.billCount, t5Avg, 'บาท'],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(prizeRows), 'Total Sale (รางวัล)');
      }

      const fileName = `bts_campaign_report_${r.campaign.currentDate}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      onError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !report) return <div className="bts-admin__section"><p>กำลังโหลด...</p></div>;
  if (!report) return <div className="bts-admin__section"><p>ไม่มีข้อมูล</p></div>;

  const { campaign, kpi, funnel } = report;
  const daysActive = Math.max(1, Math.floor((new Date(campaign.currentDate) - new Date(campaign.startDate)) / 86400000) + 1);
  const pct = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) : '0.0';

  return (
    <div className="bts-admin__section">
      <div className="bts-admin__topbar">
        <div className="bts-admin__page-title">📑 Campaign Report</div>
        <div className="bts-admin__actions">
          <button className="bts-admin__btn bts-admin__btn--dark" onClick={fetch} disabled={loading}>{loading ? 'โหลด...' : 'รีเฟรช'}</button>
          <button className="bts-admin__btn bts-admin__btn--green" onClick={exportExcel} disabled={exporting || loading}>{exporting ? 'กำลัง Export...' : '📊 Export Excel'}</button>
        </div>
      </div>

      {/* === Section 1: Overview / KPI === */}
      <h3 style={{ margin: '16px 0 8px', fontSize: 16 }}>1. ภาพรวม Campaign</h3>
      <div style={{ background:'#fff8e6', border:'1px solid #FF8A00', borderRadius:8, padding:12, marginBottom:16, fontSize:13, color:'#7a4d00' }}>
        <div style={{ fontWeight:700, marginBottom:4 }}>⏱ ข้อมูล KPI / Funnel ในรายงานนี้รวมถึง <span style={{ color:'#E24B4A' }}>{campaign.currentDate}</span> เท่านั้น (ตัดที่ 00:00 ของวันนี้ — ข้อมูลของวันนี้ยังไม่นับ)</div>
        <div>📅 เริ่ม: <b>{campaign.startDate}</b> | รวม <b>{daysActive}</b> วัน | <span style={{ color:'#888' }}>ส่วน "ผู้เล่นทั้งหมด" (Section 3) เป็น realtime อาจมีจำนวนมากกว่า KPI เพราะนับวันนี้ด้วย</span></div>
      </div>
      <div className="bts-admin__cards">
        <div className="bts-admin__card" style={{ borderColor:'#9B59B6' }}><div className="bts-admin__card-value" style={{ color:'#9B59B6' }}>{kpi.totalUniquePlayers}</div><div className="bts-admin__card-label">ผู้เล่นทั้งหมด</div></div>
        <div className="bts-admin__card" style={{ borderColor:'#459efc' }}><div className="bts-admin__card-value" style={{ color:'#459efc' }}>{report.players?.newMemberX2 ?? 0}</div><div className="bts-admin__card-label">New Member (ได้ x2)</div></div>
        <div className="bts-admin__card" style={{ borderColor:'#10C851' }}><div className="bts-admin__card-value" style={{ color:'#10C851' }}>{kpi.totalTeams}</div><div className="bts-admin__card-label">ทีมทั้งหมด</div></div>
        <div className="bts-admin__card" style={{ borderColor:'#FF8A00' }}><div className="bts-admin__card-value" style={{ color:'#FF8A00' }}>{kpi.totalTeamsComplete}</div><div className="bts-admin__card-label">ทีมครบ 4 คน</div></div>
        <div className="bts-admin__card" style={{ borderColor:'#333' }}><div className="bts-admin__card-value" style={{ color:'#333' }}>{kpi.totalBills}</div><div className="bts-admin__card-label">บิลทั้งหมด</div></div>
        <div className="bts-admin__card" style={{ borderColor:'#E24B4A' }}><div className="bts-admin__card-value" style={{ color:'#E24B4A', fontSize:20 }}>฿{kpi.totalSpend.toLocaleString()}</div><div className="bts-admin__card-label">ยอดซื้อรวม</div></div>
      </div>

      {/* === Section 2: Funnel === */}
      <h3 style={{ margin: '24px 0 8px', fontSize: 16 }}>2. Funnel การมีส่วนร่วม</h3>
      <div className="bts-admin__table-wrap">
        <table className="bts-admin__table">
          <thead><tr><th style={{ width:50 }}>#</th><th>ขั้นตอน</th><th style={{ textAlign:'right' }}>จำนวน (คน)</th><th style={{ textAlign:'right' }}>% จาก newMember</th><th style={{ textAlign:'right' }}>% conversion</th></tr></thead>
          <tbody>
            {[
              { step: '1. ผูก BigPoint (newMember)', n: funnel.linkedBigPoint, prev: funnel.linkedBigPoint },
              { step: '2. เข้าหน้า BTS', n: funnel.enteredBTS, prev: funnel.linkedBigPoint },
              { step: '3. Join / สร้างทีม', n: funnel.joinedTeam, prev: funnel.enteredBTS },
              { step: '4. สแกนบิลสำเร็จ', n: funnel.scannedSuccess, prev: funnel.joinedTeam },
              { step: '5. ทีมครบ 4 คน', n: funnel.teamsComplete, prev: funnel.joinedTeam },
            ].map((f, i) => (
              <tr key={i}>
                <td><b>{i+1}</b></td>
                <td>{f.step}</td>
                <td style={{ textAlign:'right' }}><b>{f.n.toLocaleString()}</b></td>
                <td style={{ textAlign:'right' }}>{pct(f.n, funnel.linkedBigPoint)}%</td>
                <td style={{ textAlign:'right', color:'#888' }}>{i===0?'-':`${pct(f.n, f.prev)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* === Section 3: Players (full PlayersTab) === */}
      <h3 style={{ margin: '32px 0 8px', fontSize: 16 }}>3. ผู้เล่นทั้งหมด</h3>
      <PlayersTab onError={onError} />

      {/* === Section 4: Teams (full TeamsTab, sorted: ครบ 4 ก่อน → ตามยอด) === */}
      <h3 style={{ margin: '32px 0 8px', fontSize: 16 }}>4. ทีมทั้งหมด</h3>
      <TeamsTab onError={onError} sortMode="memberThenSpend" />

      {/* === Section 5: Bills (Transactions, exclude cancelled) === */}
      <h3 style={{ margin: '32px 0 8px', fontSize: 16 }}>5. บิลทั้งหมด (ไม่รวมที่ยกเลิก)</h3>
      <TransactionsTab onError={onError} hideReverted={true} />

      {/* === Section 6: Team breakdown (ครบ/ไม่ครบ × สแกน/ไม่สแกน) === */}
      <h3 style={{ margin: '32px 0 8px', fontSize: 16 }}>6. สรุปทีม + ยอดซื้อ (แยกตามสถานะ)</h3>
      {report.teamBreakdown && (() => {
        const tb = report.teamBreakdown;
        const totalTeams = tb.complete.withBills.teamCount + tb.complete.noBills.teamCount + tb.incomplete.withBills.teamCount + tb.incomplete.noBills.teamCount;
        const totalSpend = tb.complete.withBills.totalSpend + tb.complete.noBills.totalSpend + tb.incomplete.withBills.totalSpend + tb.incomplete.noBills.totalSpend + tb.noTeam.withBills.totalSpend + tb.noTeam.noBills.totalSpend;
        return (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            {/* Total Team */}
            <div className="bts-admin__table-wrap">
              <table className="bts-admin__table">
                <thead><tr><th colSpan={3} style={{ textAlign:'center', background:'#5a8c9a', color:'#fff' }}>Total Team</th></tr>
                <tr><th>รายละเอียด</th><th style={{ textAlign:'right' }}>จำนวน</th><th>หน่วย</th></tr></thead>
                <tbody>
                  <tr style={{ fontWeight:700 }}><td>1. ตั้งชื่อทีม (ทั้งหมด)</td><td style={{ textAlign:'right' }}>{totalTeams}</td><td>ทีม</td></tr>
                  <tr style={{ background:'#e6efd9' }}><td style={{ paddingLeft:24 }}>1.1) ตั้งทีม สำเร็จ 4 คน</td><td style={{ textAlign:'right' }}>{tb.complete.withBills.teamCount + tb.complete.noBills.teamCount}</td><td>ทีม</td></tr>
                  <tr style={{ background:'#eef4e2' }}><td style={{ paddingLeft:48 }}>เริ่ม สะสม</td><td style={{ textAlign:'right' }}>{tb.complete.withBills.teamCount}</td><td>ทีม</td></tr>
                  <tr style={{ background:'#eef4e2' }}><td style={{ paddingLeft:48 }}>ไม่ สะสม</td><td style={{ textAlign:'right' }}>{tb.complete.noBills.teamCount}</td><td>ทีม</td></tr>
                  <tr style={{ background:'#fde6d9' }}><td style={{ paddingLeft:24 }}>1.2) ตั้งทีม ไม่สำเร็จ</td><td style={{ textAlign:'right' }}>{tb.incomplete.withBills.teamCount + tb.incomplete.noBills.teamCount}</td><td>ทีม</td></tr>
                  <tr style={{ background:'#fceee5' }}><td style={{ paddingLeft:48 }}>เริ่ม สะสม</td><td style={{ textAlign:'right' }}>{tb.incomplete.withBills.teamCount}</td><td>ทีม</td></tr>
                  <tr style={{ background:'#fceee5' }}><td style={{ paddingLeft:48 }}>ไม่ สะสม</td><td style={{ textAlign:'right' }}>{tb.incomplete.noBills.teamCount}</td><td>ทีม</td></tr>
                  <tr style={{ background:'#e8e8e8', fontWeight:700 }}><td>2. ไม่สร้างทีม (คน)</td><td style={{ textAlign:'right' }}>{tb.noTeam.withBills.playerCount + tb.noTeam.noBills.playerCount}</td><td>คน</td></tr>
                  <tr style={{ background:'#f0f0f0' }}><td style={{ paddingLeft:48 }}>เริ่ม สะสม</td><td style={{ textAlign:'right' }}>{tb.noTeam.withBills.playerCount}</td><td>คน</td></tr>
                  <tr style={{ background:'#f0f0f0' }}><td style={{ paddingLeft:48 }}>ไม่ สะสม</td><td style={{ textAlign:'right' }}>{tb.noTeam.noBills.playerCount}</td><td>คน</td></tr>
                </tbody>
              </table>
            </div>
            {/* Total Sale */}
            <div className="bts-admin__table-wrap">
              <table className="bts-admin__table">
                <thead><tr><th colSpan={3} style={{ textAlign:'center', background:'#d97847', color:'#fff' }}>Total Sale (ยอดซื้อที่ Scan เข้าระบบ)</th></tr>
                <tr><th>รายละเอียด</th><th style={{ textAlign:'right' }}>จำนวน</th><th>หน่วย</th></tr></thead>
                <tbody>
                  <tr style={{ fontWeight:700 }}><td>1. ยอดซื้อสะสม ทั้งหมด</td><td style={{ textAlign:'right' }}>฿{totalSpend.toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#e6efd9' }}><td style={{ paddingLeft:24 }}>1.1) ตั้งทีม สำเร็จ 4 คน</td><td style={{ textAlign:'right' }}>฿{(tb.complete.withBills.totalSpend + tb.complete.noBills.totalSpend).toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#eef4e2' }}><td style={{ paddingLeft:48 }}>เริ่ม สะสม</td><td style={{ textAlign:'right' }}>฿{tb.complete.withBills.totalSpend.toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#eef4e2' }}><td style={{ paddingLeft:48 }}>ไม่ สะสม</td><td style={{ textAlign:'right' }}>฿{tb.complete.noBills.totalSpend.toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#fde6d9' }}><td style={{ paddingLeft:24 }}>1.2) ตั้งทีม ไม่สำเร็จ</td><td style={{ textAlign:'right' }}>฿{(tb.incomplete.withBills.totalSpend + tb.incomplete.noBills.totalSpend).toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#fceee5' }}><td style={{ paddingLeft:48 }}>เริ่ม สะสม</td><td style={{ textAlign:'right' }}>฿{tb.incomplete.withBills.totalSpend.toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#fceee5' }}><td style={{ paddingLeft:48 }}>ไม่ สะสม</td><td style={{ textAlign:'right' }}>฿{tb.incomplete.noBills.totalSpend.toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#e8e8e8', fontWeight:700 }}><td>2. ไม่สร้างทีม</td><td style={{ textAlign:'right' }}>฿{(tb.noTeam.withBills.totalSpend + tb.noTeam.noBills.totalSpend).toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#f0f0f0' }}><td style={{ paddingLeft:48 }}>เริ่ม สะสม</td><td style={{ textAlign:'right' }}>฿{tb.noTeam.withBills.totalSpend.toLocaleString()}</td><td>บาท</td></tr>
                  <tr style={{ background:'#666', color:'#fff' }}><td style={{ paddingLeft:48 }}>ไม่ สะสม</td><td style={{ textAlign:'right' }}>-</td><td>บาท</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* === Section 7: ยอดสแกนแยกตามสาขา === */}
      <h3 style={{ margin: '32px 0 8px', fontSize: 16 }}>7. ยอดสแกนแยกตามสาขา</h3>
      <div style={{ background:'#f8f9fa', borderRadius:8, padding:12, marginBottom:16, fontSize:13 }}>
        <span>📍 จำนวนสาขาที่มีสแกน: <b>{(report.stores || []).length}</b> สาขา • แสดงเฉพาะ Top 10 (Export Excel จะได้ครบทุกสาขา)</span>
      </div>
      <div className="bts-admin__table-wrap">
        <table className="bts-admin__table">
          <thead><tr>
            <th style={{ width:50 }}>#</th>
            <th>รหัส</th>
            <th>สาขา</th>
            <th>Partner</th>
            <th style={{ textAlign:'right' }}>บิลทั้งหมด</th>
            <th style={{ textAlign:'right' }}>สำเร็จ</th>
            <th style={{ textAlign:'right' }}>รอคิว</th>
            <th style={{ textAlign:'right' }}>ผู้เล่น (unique)</th>
            <th style={{ textAlign:'right' }}>ยอดรวม</th>
          </tr></thead>
          <tbody>
            {(report.stores || []).slice(0, 10).map((s, i) => (
              <tr key={i}>
                <td><b>{i+1}</b></td>
                <td><b>{s.storeId}</b></td>
                <td>{s.storeName}</td>
                <td style={{ fontSize:11, color:'#666' }}>{s.partner}</td>
                <td style={{ textAlign:'right' }}>{s.totalBills}</td>
                <td style={{ textAlign:'right', color:'#10C851' }}>{s.successBills}</td>
                <td style={{ textAlign:'right', color:'#FF8A00' }}>{s.pendingBills}</td>
                <td style={{ textAlign:'right' }}>{s.uniquePlayers}</td>
                <td style={{ textAlign:'right' }}><b>฿{s.totalSpend.toLocaleString()}</b></td>
              </tr>
            ))}
            <tr className="bts-admin__row--total">
              <td colSpan={4}><b>รวมทั้งหมด ({(report.stores || []).length} สาขา)</b></td>
              <td style={{ textAlign:'right' }}><b>{(report.stores || []).reduce((s,x)=>s+x.totalBills,0)}</b></td>
              <td style={{ textAlign:'right' }}><b>{(report.stores || []).reduce((s,x)=>s+x.successBills,0)}</b></td>
              <td style={{ textAlign:'right' }}><b>{(report.stores || []).reduce((s,x)=>s+x.pendingBills,0)}</b></td>
              <td style={{ textAlign:'right' }}>-</td>
              <td style={{ textAlign:'right' }}><b>฿{(report.stores || []).reduce((s,x)=>s+x.totalSpend,0).toLocaleString()}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* === Section 8b: Total Sale (ยอดซื้อที่ Scan เข้าระบบ) — แตกตามสถานะทีม === */}
      {report.teamBreakdown && (() => {
        const tb = report.teamBreakdown;
        const c = tb.complete, ic = tb.incomplete, nt = tb.noTeam;
        const completeSpend = c.withBills.totalSpend + c.noBills.totalSpend;
        const completeBills = c.withBills.billCount + c.noBills.billCount;
        const incompleteSpend = ic.withBills.totalSpend + ic.noBills.totalSpend;
        const incompleteBills = ic.withBills.billCount + ic.noBills.billCount;
        const noTeamSpend = nt.withBills.totalSpend + nt.noBills.totalSpend;
        const noTeamBills = nt.withBills.billCount + nt.noBills.billCount;
        const grandSpend = completeSpend + incompleteSpend + noTeamSpend;
        const grandBills = completeBills + incompleteBills + noTeamBills;
        // KPI row — ใช้เฉพาะในตารางนี้ (target reference) ไม่กระทบ Section 1 KPI cards
        const KPI_SPEND = 1800000;
        const KPI_BILLS = 3000;
        const avg = (s, b) => b > 0 ? (s / b) : 0;
        const fmt = (n) => n > 0 ? n.toLocaleString(undefined, { maximumFractionDigits:2 }) : '';

        return (
          <>
            <h3 style={{ margin: '32px 0 8px', fontSize: 16 }}>Total Sale (ยอดซื้อที่ Scan เข้าระบบ)</h3>
            <div className="bts-admin__table-wrap">
              <table className="bts-admin__table">
                <thead>
                  <tr>
                    <th colSpan={6} style={{ textAlign:'center', background:'#d97c2b', color:'#fff' }}>Total Sale (ยอดซื้อที่ Scan เข้าระบบ)</th>
                  </tr>
                  <tr>
                    <th>รายละเอียด</th>
                    <th style={{ textAlign:'right' }}>จำนวน</th>
                    <th>หน่วย</th>
                    <th style={{ textAlign:'right' }}>จำนวนใบเสร็จ</th>
                    <th>หน่วย</th>
                    <th style={{ textAlign:'right' }}>Avg.Sale / ใบเสร็จ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background:'#fff' }}>
                    <td style={{ textAlign:'right', fontWeight:700 }}>KPI</td>
                    <td style={{ textAlign:'right' }}><b>{KPI_SPEND.toLocaleString()}</b></td>
                    <td><b>บาท</b></td>
                    <td style={{ textAlign:'right' }}><b>{KPI_BILLS.toLocaleString()}</b></td>
                    <td><b>ใบเสร็จ</b></td>
                    <td></td>
                  </tr>
                  <tr style={{ background:'#e8e8e8', fontWeight:700 }}>
                    <td>1. ยอดซื้อสะสม ทั้งหมด</td>
                    <td style={{ textAlign:'right' }}>{fmt(grandSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(grandBills)}</td>
                    <td>ใบเสร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(avg(grandSpend, grandBills))}</td>
                  </tr>
                  <tr style={{ background:'#e6efd9', fontWeight:600 }}>
                    <td style={{ paddingLeft:24 }}>1.1) ตั้งทีม สำเร็จ 4 คน</td>
                    <td style={{ textAlign:'right' }}>{fmt(completeSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(completeBills)}</td>
                    <td>ใบเสร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(avg(completeSpend, completeBills))}</td>
                  </tr>
                  <tr style={{ background:'#eef4e2' }}>
                    <td style={{ paddingLeft:48 }}>เริ่ม สะสม</td>
                    <td style={{ textAlign:'right' }}>{fmt(c.withBills.totalSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(c.withBills.billCount)}</td>
                    <td>ใบเสร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(avg(c.withBills.totalSpend, c.withBills.billCount))}</td>
                  </tr>
                  <tr style={{ background:'#999', color:'#fff' }}>
                    <td style={{ paddingLeft:48 }}>ไม่ สะสม</td>
                    <td style={{ textAlign:'right' }}>{fmt(c.noBills.totalSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(c.noBills.billCount)}</td>
                    <td>ใบเสร็จ</td>
                    <td></td>
                  </tr>
                  <tr style={{ background:'#fde9d9', fontWeight:600 }}>
                    <td style={{ paddingLeft:24 }}>1.2) ตั้งทีม ไม่สำเร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(incompleteSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(incompleteBills)}</td>
                    <td>ใบเสร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(avg(incompleteSpend, incompleteBills))}</td>
                  </tr>
                  <tr style={{ background:'#fdf1e6' }}>
                    <td style={{ paddingLeft:48 }}>เริ่ม สะสม</td>
                    <td style={{ textAlign:'right' }}>{fmt(ic.withBills.totalSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(ic.withBills.billCount)}</td>
                    <td>ใบเสร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(avg(ic.withBills.totalSpend, ic.withBills.billCount))}</td>
                  </tr>
                  <tr style={{ background:'#999', color:'#fff' }}>
                    <td style={{ paddingLeft:48 }}>ไม่ สะสม</td>
                    <td style={{ textAlign:'right' }}>{fmt(ic.noBills.totalSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(ic.noBills.billCount)}</td>
                    <td>ใบเสร็จ</td>
                    <td></td>
                  </tr>
                  <tr style={{ background:'#e0e0e0', fontWeight:600 }}>
                    <td>2. ไม่สร้างทีม</td>
                    <td style={{ textAlign:'right' }}>{fmt(noTeamSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(noTeamBills)}</td>
                    <td>ใบเสร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(avg(noTeamSpend, noTeamBills))}</td>
                  </tr>
                  <tr style={{ background:'#eee' }}>
                    <td style={{ paddingLeft:24 }}>เริ่ม สะสม</td>
                    <td style={{ textAlign:'right' }}>{fmt(nt.withBills.totalSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(nt.withBills.billCount)}</td>
                    <td>ใบเสร็จ</td>
                    <td style={{ textAlign:'right' }}>{fmt(avg(nt.withBills.totalSpend, nt.withBills.billCount))}</td>
                  </tr>
                  <tr style={{ background:'#999', color:'#fff' }}>
                    <td style={{ paddingLeft:24 }}>ไม่ สะสม</td>
                    <td style={{ textAlign:'right' }}>{fmt(nt.noBills.totalSpend)}</td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{fmt(nt.noBills.billCount)}</td>
                    <td>ใบเสร็จ</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        );
      })()}

      {/* === Section 8: Total Sale (เฉพาะกลุ่มที่ได้รางวัล) === */}
      {report.prizeStats && (() => {
        const ps = report.prizeStats;
        const ac = ps.allComplete || { totalSpend: 0, billCount: 0 };
        const t5 = ps.top5 || { totalSpend: 0, billCount: 0 };
        const acAvg = ac.billCount > 0 ? ac.totalSpend / ac.billCount : 0;
        const t5Avg = t5.billCount > 0 ? t5.totalSpend / t5.billCount : 0;
        return (
          <>
            <h3 style={{ margin: '32px 0 8px', fontSize: 16 }}>8. Total Sale (เฉพาะกลุ่มที่ได้รางวัล)</h3>
            <div className="bts-admin__table-wrap">
              <table className="bts-admin__table">
                <thead>
                  <tr style={{ background:'#5a8c9a', color:'#fff' }}>
                    <th colSpan={6} style={{ textAlign:'center', background:'#5a8c9a', color:'#fff' }}>Total Sale (เฉพาะกลุ่มที่ได้รางวัล)</th>
                  </tr>
                  <tr>
                    <th>รายละเอียด</th>
                    <th style={{ textAlign:'right' }}>จำนวน</th>
                    <th>หน่วย</th>
                    <th style={{ textAlign:'right' }}>จำนวนใบเสร็จ</th>
                    <th style={{ textAlign:'right' }}>Avg.Sale / ใบเสร็จ</th>
                    <th>หน่วย</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background:'#eef4e2', fontWeight:600 }}>
                    <td>1.1) ตั้งทีม สำเร็จ 4 คน</td>
                    <td style={{ textAlign:'right' }}><b>{ac.totalSpend.toLocaleString(undefined, { maximumFractionDigits:2 })}</b></td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{ac.billCount.toLocaleString()}</td>
                    <td style={{ textAlign:'right' }}>{acAvg.toLocaleString(undefined, { maximumFractionDigits:2 })}</td>
                    <td>บาท</td>
                  </tr>
                  <tr style={{ background:'#e6efd9', fontWeight:600 }}>
                    <td style={{ paddingLeft:24 }}>ยอดคะแนนสะสม {ps.topN} ทีมที่ได้รางวัล</td>
                    <td style={{ textAlign:'right' }}><b>{t5.totalSpend.toLocaleString(undefined, { maximumFractionDigits:2 })}</b></td>
                    <td>บาท</td>
                    <td style={{ textAlign:'right' }}>{t5.billCount.toLocaleString()}</td>
                    <td style={{ textAlign:'right' }}>{t5Avg.toLocaleString(undefined, { maximumFractionDigits:2 })}</td>
                    <td>บาท</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default CampaignReportTab;
